<template>
  <div>
    <b-container fluid>
      <h1>Change Request Status</h1>
      <b-row>
        <b-col cols="12" lg="4">
          <BaseTextInput v-model="criteria.requestId"
                         :v="$v.criteria.requestId"
                         label="Tracking or Job ID"
                         tip=""
                         slug="requestId"/>
        </b-col>
        <b-col cols="12" lg="4">
          <BaseDivisionSelector v-model="criteria.division"
                                :v="$v.criteria.division"
                                slug="division"
                                label="Division"/>
        </b-col>
        <b-col cols="12" lg="4">
          <b-form-group
              label="Staffing Specialist" label-for="specialist-select"
              invalid-feedback="Staffing Specialist is required">
            <b-form-select id="specialist-select"
                           v-model="criteria.assignedTo"
                           :options="specialists"
                           value-field="userId"
                           text-field="name"/>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button type="button" variant="primary" @click="submit">
            <b-spinner small v-show="loading" class="mr-2"></b-spinner>
            Submit
          </b-button>
        </b-col>
      </b-row>
      <hr/>
      <div v-if="this.requests.length > 0">
        <b-table hover small responsive show-empty
                 id="manage-position-request-status"
                 :fields="fields"
                 :items="requests"
                 primary-key="mdhTrackingId"
                 :busy="loading"
                 :sort-by.sync="sortBy"
                 :sort-desc.sync="sortDesc">
          <template #table-caption></template>
          <template v-slot:cell(actions)="row">
            <b-button size="sm" @click="editItem(row.item)" class="mr-1">
              edit
            </b-button>
          </template>
          <template v-slot:cell(mdhTrackingId)="data">
            <span>{{ data.item.mdhTrackingId }}</span>
            <span v-if="data.item.jobId" style="font-size:.90em;">
                    <br/> {{ data.item.jobId }}
                </span>
          </template>


          <template v-slot:no-data>
            <v-btn color="primary" @click="initialize">Reset</v-btn>
          </template>

          <template v-slot:empty="scope">
            <p>No requests found.</p>
          </template>

          <div slot="table-busy" class="text-center text-danger my-2">
            <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"></b-spinner>
            <strong>Loading, Please Wait...</strong>
          </div>
        </b-table>
      </div>

      <b-modal id="modal-crud" hide-header hide-footer>
        <h2>Change Request Status</h2>
        <hr/>
        <b-row>
          <b-col>
            <BaseTextOutput :value=" editedItem.previous ? editedItem.previous + ' ('+currentRequestStatus.text+')' : currentRequestStatus.text"
                            label="Current Request Status"
                            v-if="currentRequestStatus"/>


          </b-col>
        </b-row>
        <form ref="form" @submit.prevent="update">
          <b-form-group
              :state="$v.editedItem.$dirty ? !$v.editedItem.$error : null"
              label="Change Request Status To:"
              label-for="request-status-select"
              invalid-feedback="request-status-select">
            <b-form-select id="request-status-select" v-model="editedItem.value"
                           :state="$v.editedItem.$dirty ? !$v.editedItem.$error : null"
                           :options="limitedStatusOptions">
              <template v-slot:first>
                <option :value="null" disabled>-- Please select an option --</option>
              </template>
            </b-form-select>

          </b-form-group>

          <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
          <b-button @click="closeForm" type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
        </form>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios';
import axiosRetry from 'axios-retry';
import pageTitle from '@/mixins/pageTitle'
import {
  required,
  email,
  minLength,
  maxLength,
  numeric,
  requiredUnless,
  requiredIf,
  ref,
  minValue,
  maxValue
} from 'vuelidate/lib/validators'
import _ from "lodash"
import {statusOptions} from "@/mixins/statusOptions";
import {roleService} from "@/mixins/roleService";

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay
});

export default {
  title: 'Change Request Status',
  mixins: [
      statusOptions,
      pageTitle,
      roleService,
  ],
  data: () => ({
    requests: [],
    specialists: [],
    loading: false,
    criteria: {
      division: null,
      requestId: null,
      assignedTo: null,
      staffingSpecialistOnly: true,
    },

    perPage: 15,
    currentPage: 1,
    totalRows: 0,

    sortBy: 'lastUpdatedDateTime',
    sortDesc: true,

    fields: [
      {key: 'mdhTrackingId', sortable: true, label: 'Number'},
      {key: 'divisionCode', sortable: true, label: 'Division'},
      {key: 'assignedName', sortable: true, label: 'Assigned To'},
      {key: 'requestStatus', sortable: true, label: 'Status'},
      {key: 'actions', label: 'Actions'},
    ],
    currentRequestStatus: null,
    editedIndex: -1,
    editedItem: {
      value: null,
      text: null,
    },
    defaultItem: {
      value: null,
      text: null,
    },
  }),

  validations() {
    return {
      criteria: {
        division: {required: false, maxLength: maxLength(100)},
        requestId: {required: false, maxLength: maxLength(20)},
      },
      editedItem: {required: false},
    }
  },
  mounted() {
    this.fetchSpecialists()
  },

  computed: {
    limitedStatusOptions() {
      if(this.currentRequestStatus) {
        let hasAdminRole = this.isAdmin() || this.isLead()
        if (hasAdminRole) {
          let returnData = []
          for (var idx in this.hrmAdminOptions) {
            let currData = this.hrmAdminOptions[idx]
            if(currData.value == this.currentRequestStatus.value) {
              continue
            } else {
              returnData.push(currData)
            }
          }
          return returnData
        } else {
          this.data = this.staffingStatusOptions.slice(0, this.staffingStatusOptions.indexOf(this.currentRequestStatus))

        }
      }
      return []
      }

  },


  methods: {

    fetchSpecialists() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/organization/specialist`)
          .then(response => {
            this.specialists = response.data;
            const emptySpecialist = {
              name: "-- Please select an option --"
            }
            this.specialists.unshift(emptySpecialist)
          });
    },
    submit() {
      this.loading = true
      axios.post(`${process.env.VUE_APP_API_BASE_URL}/report/search`, this.criteria)
          .then(response => {
            this.requests = response.data
            this.loading = false
            this.totalRows = response.data.size()
          })
    },
    editItem(item) {
      this.editedIndex = this.requests.indexOf(item);
      if(item.previousRequestStatusBeforeHoldOrCancel){
        this.currentRequestStatus = this.getStatusCodeFromValue(item.previousRequestStatusBeforeHoldOrCancel)
        this.editedItem = Object.assign({previous: this.getStatusCodeFromDescription(item.requestStatus).text}, this.getStatusCodeFromValue(item.previousRequestStatusBeforeHoldOrCancel));
      }
      else{
        this.currentRequestStatus = this.getStatusCodeFromDescription(item.requestStatus)
        this.editedItem = Object.assign({}, this.getStatusCodeFromDescription(item.requestStatus));
      }
      this.$bvModal.show('modal-crud');
    },
    close() {
      this.$bvModal.hide('modal-crud');
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.currentRequestStatus = null;
      }, 300)
    },
    closeForm() {
        this.$bvModal.hide('modal-crud');
    },
    update() {
      console.log("We are updating a thing")
      this.requests[this.editedIndex].requestStatus = this.getStatusCodeFromValue(this.editItem.value)
      axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/transition`,
          {
            id: this.requests[this.editedIndex].id,
            transitionStatusTo: this.editedItem.value
          }
      )
          .then(response => {
            this.$bvToast.toast('HRM Request Status has been updated', {
              title: 'Status',
              toaster: 'b-toaster-top-center',
              solid: true,
              variant: 'success',
              appendToast: true
            });
            this.requests[this.editedIndex].requestStatus = this.getStatusCodeFromValue(this.editedItem.value).text
            this.close();
          });
    },
    getStatusCodeFromDescription(description) {
      //filter gives us an array, so we pick up the first option
      return this.staffingStatusOptions.filter(status => status.text == description)[0];
    },
    getStatusCodeFromValue(value) {
      //filter gives us an array, so we pick up the first option
      return this.staffingStatusOptions.filter(status => status.value == value)[0];
    },
  }
}
</script>